import axios from 'axios';
import { Api } from 'Utils/api';
import { generateUUID } from 'Utils/helpers';

type ProcessingServiceConfig = {
  service?: string;
  url?: string;
  apiKey?: string;
};

const CONFIG_CACHE_TTL_MS = 5 * 60 * 1000;
let cachedConfig: ProcessingServiceConfig | null = null;
let cachedAt: number | null = null;

const logProcessingConfig = (message: string, meta: Record<string, unknown> = {}) => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info('[image-processor-config]', message, meta);
  }
};

export const getProcessingServiceConfig = async (
  forceRefresh = false
): Promise<ProcessingServiceConfig | null> => {
  const isCacheValid = cachedConfig && cachedAt && Date.now() - cachedAt < CONFIG_CACHE_TTL_MS;
  if (!forceRefresh && isCacheValid) {
    logProcessingConfig('Using cached config', { service: cachedConfig?.service, url: cachedConfig?.url });
    return cachedConfig;
  }

  const response = await Api.get('/configuration/processing-service');
  const data = response?.data || response;
  const configData = data?.data || data;

  if (configData) {
    cachedConfig = {
      service: configData.service,
      url: configData.url || configData.processing_url || configData.endpoint,
      apiKey: configData.apiKey || configData.api_key,
    };
    cachedAt = Date.now();
    logProcessingConfig('Fetched config', { service: cachedConfig.service, url: cachedConfig.url });
  } else {
    cachedConfig = null;
    cachedAt = null;
    logProcessingConfig('No processing service config found');
  }

  return cachedConfig;
};

export const createRoomImageProcessorAssembly = async (
  roomId: number,
  payload: {
    assembly_id: string;
    total_files: number;
    project_id: number;
    room_id: number;
    group_uuid: string;
    bytes_received: number;
    photo_names: string[];
    albums?: Record<number, string[]>;
    ir_photos?: any[];
    order?: string[];
    notes?: Record<string, string[]>;
  }
) => {
  const response = await Api.post(`/rooms/${roomId}/image-processor`, payload);
  return response?.data?.data || response?.data || response;
};

export const createEntityImageProcessorAssembly = async (
  payload: {
    assembly_id: string;
    total_files: number;
    entity_type: string;
    entity_id: number;
    group_uuid: string;
    bytes_received: number;
    photo_names: string[];
    albums?: Record<number, string[]>;
    ir_photos?: any[];
    order?: string[];
    notes?: Record<string, string[]>;
  }
) => {
  const response = await Api.post('/image-processor', payload);
  return response?.data?.data || response?.data || response;
};

export const uploadFileToProcessingService = async (
  processingUrl: string,
  file: File,
  assemblyId: string,
  apiKey?: string
) => {
  const headers: Record<string, string> = {
    'X-Assembly-Id': assemblyId,
    'Content-Type': file.type,
  };

  if (apiKey) {
    headers['x-api-key'] = apiKey;
  }

  const uploadUrl = `${processingUrl}?filename=${encodeURIComponent(file.name)}`;

  return axios.post(uploadUrl, file, {
    headers,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    timeout: 300000, // 5 minutes for large files
  });
};

export const buildImageProcessorPayload = ({
  roomId,
  albumId,
  projectId,
  files,
}: {
  roomId: number;
  albumId?: number;
  projectId: number;
  files: File[];
}) => {
  const assemblyId = generateUUID();
  const groupUuid = generateUUID();

  const photoNames = files.map((file) => file.name);
  const bytesReceived = files.reduce((total, file) => total + (file.size || 0), 0);

  const payload = {
    assembly_id: assemblyId,
    total_files: files.length,
    project_id: projectId,
    room_id: roomId,
    group_uuid: groupUuid,
    bytes_received: bytesReceived,
    photo_names: photoNames,
    albums: albumId ? { [albumId]: photoNames } : undefined,
    ir_photos: [],
    order: photoNames,
    notes: {} as Record<string, string[]>,
  };

  return payload;
};
